import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  Volume2,
  VolumeX,
  Megaphone,
  MegaphoneOff,
  Gem,
} from "lucide-react";
import { Card } from "@/types/linguadeck";
import { useGamification } from "@/contexts/GamificationContext";
import { playAudio } from "@/lib/audioUtils";
import { useSoundStore } from "@/stores/useSoundStore";
import { playSuccess, playError } from "@/lib/studySfx";
import { useVictoryMusic } from "@/hooks/useVictoryMusic";
import { GameResultsScreen } from "@/components/study/GameResultsScreen";

const INITIAL_TIME_SEC = 60;
const CORRECT_BONUS_SEC = 2;
const WRONG_PENALTY_SEC = 3;
const CRYSTALS_BASE = 5;
const LOW_TIME_THRESHOLD = 10;

function shuffleArray<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/** Pick a wrong translation from the deck (not this card's back). */
function getWrongBackFor(card: Card, pool: Card[]): string {
  const correct = (card.back || "").trim();
  const others = pool.filter((c) => (c.back || "").trim() !== correct);
  if (others.length === 0) return correct;
  const pick = others[Math.floor(Math.random() * others.length)];
  return (pick?.back || "").trim() || correct;
}

/** Single blitz card data: term + displayed translation (correct or wrong). */
interface BlitzCardState {
  card: Card;
  displayTranslation: string;
  isCorrect: boolean;
}

interface BlitzRushModeProps {
  cards: Card[];
  lang?: string;
  onBack?: () => void;
  onProgressComplete?: (pct: number) => void;
}

/** Floating +N crystals. */
function FloatingCrystals({
  amount,
  onComplete,
}: {
  amount: number;
  onComplete: () => void;
}) {
  return (
    <motion.div
      className="pointer-events-none fixed inset-0 flex items-center justify-center z-50"
      initial={false}
    >
      <motion.span
        className="inline-flex items-center gap-1.5 text-2xl font-bold text-[hsl(var(--accent2))] drop-shadow-lg whitespace-nowrap"
        initial={{ opacity: 1, y: 0, scale: 1 }}
        animate={{ opacity: 0, y: -80, scale: 1.2 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        onAnimationComplete={onComplete}
      >
        +{amount}
        <Gem className="w-6 h-6" aria-hidden />
      </motion.span>
    </motion.div>
  );
}

export default function BlitzRushMode({
  cards,
  lang: deckLang,
  onBack,
  onProgressComplete,
}: BlitzRushModeProps) {
  const { t: tFn } = useTranslation();
  const t = typeof tFn === "function" ? tFn : ((key: string, opts?: object) => key);
  const { addCrystals, finishStudySession } = useGamification();
  const {
    isSoundEnabled,
    isVoiceEnabled,
    gameVolume,
    voiceVolume,
    setGameVolume,
    setVoiceVolume,
    toggleSound,
    toggleVoice,
  } = useSoundStore();

  const filteredCards = useMemo(
    () =>
      cards.filter((c) => (c.front || "").trim() && (c.back || "").trim()),
    [cards]
  );

  const [timeLeft, setTimeLeft] = useState(INITIAL_TIME_SEC);
  const [combo, setCombo] = useState(0);
  const [sessionCrystals, setSessionCrystals] = useState(0);
  const [cardIndex, setCardIndex] = useState(0);
  const [currentBlitzCard, setCurrentBlitzCard] = useState<BlitzCardState | null>(null);
  const [floatingCrystals, setFloatingCrystals] = useState<number | null>(null);
  const [isGameFinished, setIsGameFinished] = useState(false);
  const [gameEndReason, setGameEndReason] = useState<"win" | "timeout" | null>(
    null
  );
  const [shake, setShake] = useState(false);
  const [exitDirection, setExitDirection] = useState<"left" | "right" | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const shuffledDeckRef = useRef<Card[]>([]);
  const isGameFinishedRef = useRef(false);

  useEffect(() => {
    isGameFinishedRef.current = isGameFinished;
  }, [isGameFinished]);

  useVictoryMusic({
    isGameFinished,
    isSoundEnabled,
    gameVolume,
    endResult: gameEndReason ?? undefined,
  });


  const isLowTime = timeLeft <= LOW_TIME_THRESHOLD;
  const progressPct = Math.max(0, (timeLeft / INITIAL_TIME_SEC) * 100);

  const pickNextBlitzCard = useCallback(
    (index: number): BlitzCardState | null => {
      const deck = shuffledDeckRef.current;
      if (!deck.length || index >= deck.length) return null;
      const card = deck[index];
      const correctBack = (card.back || "").trim();
      const useCorrect = Math.random() < 0.5;
      const displayTranslation = useCorrect
        ? correctBack
        : getWrongBackFor(card, deck);
      return {
        card,
        displayTranslation,
        isCorrect: displayTranslation === correctBack,
      };
    },
    []
  );

  const advanceToNextCard = useCallback(() => {
    const nextIndex = cardIndex + 1;
    const total = shuffledDeckRef.current.length;
    if (nextIndex >= total) {
      if (total > 0 && onProgressComplete) onProgressComplete(100);
      setGameEndReason("win");
      setIsGameFinished(true);
      setCurrentBlitzCard(null);
      return;
    }
    if (total > 0 && onProgressComplete) {
      const pct = Math.min(100, Math.round((nextIndex / total) * 100));
      onProgressComplete(pct);
    }
    setCardIndex(nextIndex);
    const next = pickNextBlitzCard(nextIndex);
    setCurrentBlitzCard(next);
    if (next && useSoundStore.getState().isVoiceEnabled && (next.card.front || "").trim()) {
      if (isGameFinishedRef.current) return;
      void playAudio({ card: next.card, side: "front", deckLang: deckLang ?? "en", volume: voiceVolume });
    }
  }, [cardIndex, pickNextBlitzCard, voiceVolume, deckLang, onProgressComplete]);

  useEffect(() => {
    if (filteredCards.length === 0) return;
    if (isGameFinishedRef.current) return;
    shuffledDeckRef.current = shuffleArray(filteredCards);
    const first = pickNextBlitzCard(0);
    setCurrentBlitzCard(first);
    setCardIndex(0);
  }, [filteredCards, pickNextBlitzCard]);

  const handleStart = useCallback(() => {
    setHasStarted(true);
    if (currentBlitzCard && useSoundStore.getState().isVoiceEnabled && (currentBlitzCard.card.front || "").trim()) {
      void playAudio({
        card: currentBlitzCard.card,
        side: "front",
        deckLang: deckLang ?? "en",
        volume: voiceVolume,
      });
    }
  }, [currentBlitzCard, voiceVolume, deckLang]);

  useEffect(() => {
    if (!hasStarted || isGameFinished || !currentBlitzCard) return;
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          const total = shuffledDeckRef.current.length;
          if (total > 0 && onProgressComplete) {
            const pct = Math.min(100, Math.round((cardIndex / total) * 100));
            onProgressComplete(pct);
          }
          setGameEndReason("timeout");
          setIsGameFinished(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [hasStarted, isGameFinished, currentBlitzCard, cardIndex, onProgressComplete]);

  const handleAnswer = useCallback(
    (userSaidTrue: boolean) => {
      if (!currentBlitzCard || isTransitioning) return;
      const correct = currentBlitzCard.isCorrect;
      const userCorrect = userSaidTrue === correct;

      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }

      setIsTransitioning(true);
      setExitDirection(userSaidTrue ? "right" : "left");

      if (userCorrect) {
        if (isSoundEnabled) playSuccess(gameVolume * 0.7);
        setTimeLeft((prevTime) => prevTime + CORRECT_BONUS_SEC);
        const newCombo = combo + 1;
        setCombo(newCombo);
        const amount = CRYSTALS_BASE * newCombo;
        const result = addCrystals(amount, t("blitzRush.rewardReason"), {
          isLearningAction: true,
          silent: true,
        });
        const totalAdd = typeof result === "object" ? result.totalAdd : amount;
        setFloatingCrystals(totalAdd);
        setSessionCrystals((s) => s + totalAdd);
        finishStudySession(1, false);
      } else {
        if (isSoundEnabled) playError(gameVolume * 0.8);
        setTimeLeft((prevTime) => Math.max(0, prevTime - WRONG_PENALTY_SEC));
        setCombo(0);
        setShake(true);
        setTimeout(() => setShake(false), 500);
        if (timeLeft <= WRONG_PENALTY_SEC) {
          setTimeout(() => {
            const total = shuffledDeckRef.current.length;
            if (total > 0 && onProgressComplete) {
              const pct = Math.min(100, Math.round((cardIndex / total) * 100));
              onProgressComplete(pct);
            }
            setGameEndReason("timeout");
            setIsGameFinished(true);
          }, 400);
        }
      }

      setTimeout(() => {
        setExitDirection(null);
        if (timeLeft <= WRONG_PENALTY_SEC && !userCorrect) {
          setIsTransitioning(false);
          return;
        }
        advanceToNextCard();
        setIsTransitioning(false);
      }, 380);
    },
    [
      currentBlitzCard,
      isTransitioning,
      combo,
      timeLeft,
      isSoundEnabled,
      gameVolume,
      addCrystals,
      finishStudySession,
      t,
      advanceToNextCard,
    ]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isGameFinished || !currentBlitzCard || isTransitioning) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        handleAnswer(false);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        handleAnswer(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isGameFinished, currentBlitzCard, isTransitioning, handleAnswer]);

  const handlePlayAgain = useCallback(() => {
    setHasStarted(false);
    setIsGameFinished(false);
    isGameFinishedRef.current = false;
    setGameEndReason(null);
    setTimeLeft(INITIAL_TIME_SEC);
    setCombo(0);
    setSessionCrystals(0);
    shuffledDeckRef.current = shuffleArray(filteredCards);
    const first = pickNextBlitzCard(0);
    setCurrentBlitzCard(first);
    setCardIndex(0);
    if (first && useSoundStore.getState().isVoiceEnabled && (first.card.front || "").trim()) {
      void playAudio({ card: first.card, side: "front", deckLang: deckLang ?? "en", volume: voiceVolume });
    }
  }, [filteredCards, pickNextBlitzCard, voiceVolume, deckLang]);

  const headerRow = (
    <header className="flex justify-between items-center w-full max-w-2xl mx-auto mb-6 gap-4">
      <div className="min-w-0 flex-1">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] hover:bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] font-medium transition-colors"
            aria-label={t("studyMenu.backToDeck")}
          >
            <ChevronLeft className="w-4 h-4 shrink-0" aria-hidden />
            {t("studyMenu.backToDeck")}
          </button>
        ) : (
          <div />
        )}
      </div>
      <div className="grid grid-cols-[64px_auto] gap-x-2 gap-y-1 items-center shrink-0">
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={gameVolume}
          onChange={(e) => setGameVolume(parseFloat(e.target.value))}
          className="w-16 h-1.5 accent-[hsl(var(--primary))] cursor-pointer justify-self-end"
          aria-label={t("common.gameVolume")}
        />
        <button
          type="button"
          onClick={toggleSound}
          className={`p-2 rounded-lg hover:bg-[hsl(var(--bg3))] transition-colors w-9 h-9 flex items-center justify-center ${isSoundEnabled ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))] opacity-50"}`}
          aria-label={
            isSoundEnabled ? t("common.muteMusic") : t("common.unmuteMusic")
          }
        >
          {isSoundEnabled ? (
            <Volume2 className="w-5 h-5" />
          ) : (
            <VolumeX className="w-5 h-5" />
          )}
        </button>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={voiceVolume}
          onChange={(e) => setVoiceVolume(parseFloat(e.target.value))}
          className="w-16 h-1.5 accent-[hsl(var(--primary))] cursor-pointer justify-self-end"
          aria-label={t("common.voiceVolume")}
        />
        <button
          type="button"
          onClick={toggleVoice}
          className={`p-2 rounded-lg hover:bg-[hsl(var(--bg3))] transition-colors w-9 h-9 flex items-center justify-center ${isVoiceEnabled ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))] opacity-50"}`}
          aria-label={
            isVoiceEnabled
              ? t("common.voiceModeOn")
              : t("common.voiceModeOff")
          }
        >
          {isVoiceEnabled ? (
            <Megaphone className="w-5 h-5" />
          ) : (
            <MegaphoneOff className="w-5 h-5" />
          )}
        </button>
      </div>
    </header>
  );

  if (filteredCards.length === 0) {
    return (
      <div className="flex flex-col w-full max-w-lg mx-auto px-4">
        {headerRow}
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] p-8 text-center">
          <p className="text-lg font-medium text-[hsl(var(--foreground))]">
            {t("blitzRush.needCards")}
          </p>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
            {t("blitzRush.addMoreCards")}
          </p>
        </div>
      </div>
    );
  }

  // When game is over, render only the results screen so game board is fully unmounted (no phantom hover/TTS).
  if (isGameFinished) {
    return (
      <GameResultsScreen
        sessionCrystals={sessionCrystals}
        onPlayAgain={handlePlayAgain}
        onBackToDeck={onBack ?? (() => {})}
        headerRow={headerRow}
        variant={gameEndReason === "timeout" ? "timeOut" : "victory"}
      />
    );
  }

  // Game board: only rendered when !isGameFinished so it never receives events while results are shown.
  return (
    <div className="relative flex flex-col w-full max-w-lg mx-auto px-4 min-h-[calc(100vh-6rem)] pb-10">
      <AnimatePresence>
        {floatingCrystals != null && (
          <FloatingCrystals
            amount={floatingCrystals}
            onComplete={() => setFloatingCrystals(null)}
          />
        )}
      </AnimatePresence>

      {headerRow}

      {!hasStarted && (
        <button
          type="button"
          onClick={handleStart}
          className="absolute inset-0 z-20 flex items-center justify-center bg-black/40 backdrop-blur-sm rounded-2xl cursor-pointer hover:bg-black/50 transition-colors"
          aria-label={t("blitzRush.clickToStart", { defaultValue: "Click to start" })}
        >
          <span className="text-xl font-semibold text-white drop-shadow-lg px-6 py-3 rounded-xl bg-white/10 border border-white/20">
            {t("blitzRush.clickToStart", { defaultValue: "Click to start" })}
          </span>
        </button>
      )}

      {/* Neon timer bar */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">
            {t("time_label", "TIME")}
          </span>
          <span
            className={`text-lg font-bold tabular-nums transition-colors ${
              isLowTime
                ? "text-red-400 dark:text-red-400"
                : "text-emerald-600 dark:text-cyan-400"
            }`}
            style={{
              textShadow: isLowTime
                ? "0 0 12px rgba(248,113,113,0.8), 0 0 24px rgba(248,113,113,0.4)"
                : "0 0 12px rgba(52,211,153,0.5), 0 0 24px rgba(34,211,238,0.3)",
            }}
          >
            {timeLeft}s
          </span>
        </div>
        <div className="relative h-2.5 w-full rounded-full overflow-hidden bg-[hsl(var(--border))]/80">
          <motion.div
            className="h-full rounded-full"
            style={{
              background: isLowTime
                ? "linear-gradient(90deg, #f87171 0%, #dc2626 50%, #991b1b 100%)"
                : "linear-gradient(90deg, #34d399 0%, #22d3ee 50%, #06b6d4 100%)",
              boxShadow: isLowTime
                ? "0 0 20px rgba(248,113,113,0.6), inset 0 0 10px rgba(255,255,255,0.2)"
                : "0 0 20px rgba(52,211,153,0.4), inset 0 0 10px rgba(255,255,255,0.15)",
            }}
            initial={false}
            animate={{
              width: `${progressPct}%`,
            }}
            transition={{ type: "tween", duration: 0.3 }}
          />
          {isLowTime && (
            <motion.div
              className="absolute inset-0 rounded-full bg-red-500/30"
              animate={{ opacity: [0.3, 0.7, 0.3] }}
              transition={{ duration: 0.8, repeat: Infinity }}
              style={{ pointerEvents: "none" }}
            />
          )}
        </div>
      </div>

      {/* Combo */}
      {combo > 0 && (
        <motion.p
          className="text-center text-sm font-bold text-[hsl(var(--accent2))] mb-4"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 400, damping: 25 }}
        >
          {t("blitzRush.combo", { count: combo })}
        </motion.p>
      )}

      {/* Card stack area */}
      <div className="flex-1 flex flex-col items-center justify-center min-h-[24rem] relative">
        <motion.div
          className="w-full max-w-lg mx-auto relative flex justify-center items-center"
          animate={shake ? { x: [0, -8, 8, -8, 8, 0] } : {}}
          transition={{ duration: 0.4 }}
        >
          <AnimatePresence mode="wait">
            {currentBlitzCard && (
              <motion.div
                key={`${currentBlitzCard.card.id ?? cardIndex}-${cardIndex}`}
                className="absolute w-[92%] max-w-md min-h-[24rem] rounded-2xl overflow-hidden border-2 border-white/20 shadow-2xl flex flex-col"
                style={{
                  background:
                    "linear-gradient(145deg, rgba(30,27,75,0.95) 0%, rgba(15,10,45,0.98) 100%)",
                  boxShadow:
                    "0 0 40px rgba(168,85,247,0.25), 0 20px 50px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)",
                }}
                initial={{
                  scale: 0.85,
                  opacity: 0.6,
                  x: 0,
                }}
                animate={{
                  scale: 1,
                  opacity: 1,
                  x: 0,
                }}
                exit={{
                  x: exitDirection === "left" ? -400 : exitDirection === "right" ? 400 : 0,
                  opacity: 0,
                  scale: 0.9,
                  transition: { duration: 0.35, ease: "easeIn" },
                }}
                transition={{
                  type: "spring",
                  stiffness: 320,
                  damping: 28,
                }}
              >
                <div className="p-8 sm:p-11 text-center flex flex-col items-center justify-center flex-1">
                  <p className="text-xs font-semibold uppercase tracking-widest text-white/50 mb-3">
                    {t("term", "TERM")}
                  </p>
                  <p
                    className="text-2xl sm:text-3xl font-bold text-white mb-6 drop-shadow-lg"
                    style={{
                      textShadow:
                        "0 0 20px rgba(168,85,247,0.4), 0 2px 4px rgba(0,0,0,0.3)",
                    }}
                  >
                    {(currentBlitzCard.card.front || "").trim() || "—"}
                  </p>
                  <p className="text-xs font-semibold uppercase tracking-widest text-white/50 mb-2">
                    {t("translation", "TRANSLATION")}
                  </p>
                  <p className="text-xl sm:text-2xl font-semibold text-white/90">
                    {currentBlitzCard.displayTranslation || "—"}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* True / False buttons */}
      <div className="flex gap-4 justify-center mt-8 px-2">
        <motion.button
          type="button"
          onClick={() => handleAnswer(false)}
          disabled={!currentBlitzCard || isTransitioning}
          className="flex-1 max-w-[160px] py-4 px-6 rounded-2xl font-bold text-xl uppercase tracking-wider border-2 border-red-400/60 bg-red-500/20 text-red-600 dark:text-red-400 hover:bg-red-500/30 focus:outline-none focus:ring-2 focus:ring-red-400/50 disabled:opacity-50 disabled:pointer-events-none transition-colors"
          style={{
            boxShadow:
              "0 0 24px rgba(248,113,113,0.3), inset 0 1px 0 rgba(255,255,255,0.1)",
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {t("false_button", "FALSE")}
        </motion.button>
        <motion.button
          type="button"
          onClick={() => handleAnswer(true)}
          disabled={!currentBlitzCard || isTransitioning}
          className="flex-1 max-w-[160px] py-4 px-6 rounded-2xl font-bold text-xl uppercase tracking-wider border-2 border-emerald-400/60 bg-emerald-500/20 text-green-600 dark:text-green-400 hover:bg-emerald-500/30 focus:outline-none focus:ring-2 focus:ring-emerald-400/50 disabled:opacity-50 disabled:pointer-events-none transition-colors"
          style={{
            boxShadow:
              "0 0 24px rgba(52,211,153,0.3), inset 0 1px 0 rgba(255,255,255,0.1)",
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {t("true_button", "TRUE")}
        </motion.button>
      </div>
      <p className="text-center text-xs text-[hsl(var(--muted-foreground))] mt-3">
        {t("blitz_keyboard_hint", "← Left = False · Right = True →")}
      </p>
    </div>
  );
}
